import * as React from "react";
import {
  Callout,
  Link,
  Tabs,
  useMediaQuery,
  useTheme,
  AppShell,
  PrimarySecondaryPage,
  FullWidthPage,
} from "@wayflyer/flyui";
import type { LegacyTableProps } from "@wayflyer/flyui-dataviz";
import { DatePickerHeader } from "@wayflyer/flyui-dataviz";
import { useIntl } from "react-intl";
import { styled } from "styled-components";

import { InsightsDataTable } from "../../components/insights-data-table";
import { ProfitChart } from "../../components/profitability-chart";
import type { OverviewProps } from "../../interface";
import {
  DataGroupContainer,
  PinnedMetricsTabContainer,
  ProfitabilityChartsContainer,
  Spinner,
} from "../../components/styled";
import { SummaryPanel } from "../../components/summary-panel";
import { AdditionalCostsForm } from "../additional-costs-form";
import { PinnedMetrics } from "../../components/pinned-metrics";
import { OverviewPlaceholder } from "../../components/overview-placeholder/overview-placeholder";
import { RoasWarning } from "../../components/roas-warning/roas-warning";

import { PanelContainer, PanelPadding } from "./styled";

const LearnMoreBox = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 8px 0px;
  gap: 4px;
`;

/**
 * Overview component
 */
export const Overview = ({
  chartDataLoading = false,
  chartDataError = "",
  summaryDataLoading = false,
  summaryDataError = "",
  tableDataLoading = false,
  tableDataError = "",
  tableProps,
  subtitle = "",
  hint = "",
  chartData,
  thisYearEnabled = true,
  lastYearEnabled = false,
  salesEnabled = true,
  salesIncReturnsEnabled = true,
  profitEnabled = false,
  marketingEnabled = true,
  roasEnabled = true,
  aovEnabled = true,
  cacEnabled = true,
  chartFilters,
  pageTitle,
  onSelectMetric,
  summaryData,
  additionalCostsData,
  additionalCostsDataError = "",
  datePickerHeaderProps,
  enableTabs = false,
  pinnedMetricsProps,
  tabTitlePinnedMetrics,
  tabTitleTable,
  showAmazonPlaceholder = false,
  isAmazonCustomer = false,
  supportLink = "",
  learnMoreLink = "",
}: OverviewProps): UIElement => {
  const intl = useIntl();
  const query = useMediaQuery();
  const theme = useTheme();
  const vsLastYear = intl.formatMessage({ id: "Overview 'vs last year' suffix", defaultMessage: "vs last year" });
  const [selectedThisYearEnabled, setSelectedThisYearEnabled] = React.useState(thisYearEnabled);
  const [selectedLastYearEnabled, setSelectedLastYearEnabled] = React.useState(lastYearEnabled);

  React.useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
    setSelectedThisYearEnabled(thisYearEnabled);
    setSelectedLastYearEnabled(lastYearEnabled);
  }, [thisYearEnabled, lastYearEnabled]);

  if (showAmazonPlaceholder) {
    return (
      <AppShell>
        <PrimarySecondaryPage
          title="Overview"
          primaryContent={
            <>
              <div>
                <OverviewPlaceholder supportLink={supportLink} />
              </div>
              <LearnMoreBox>
                <Link url={learnMoreLink} external>
                  {intl.formatMessage({
                    defaultMessage: "Learn more about insights",
                    id: "overview placeholder: learn more",
                  })}
                </Link>
              </LearnMoreBox>
            </>
          }
        />
      </AppShell>
    );
  }

  const tableDataLoaded = !tableDataLoading && !tableDataError;
  const hasMainError = chartDataError || summaryDataError;

  const { onClickDateRes, hideMonthly, hideWeekly, selectedButtonIndex, ...rest } = chartFilters;

  const temporalResPickerProps = { onClickDateRes, hideWeekly, hideMonthly, selectedButtonIndex };

  const chartFilterSwitchesProps = {
    switchOneColor: theme.palette.bg.accent.strong,
    switchTwoColor: theme.palette.bg.accent.strong,
    ...rest,
    switchOneOn: selectedThisYearEnabled,
    switchTwoOn: selectedLastYearEnabled,
  };

  return (
    <AppShell>
      <FullWidthPage title={pageTitle}>
        <RoasWarning isAmazonCustomer={isAmazonCustomer} roasEnabled={roasEnabled} />
        <DataGroupContainer>
          {datePickerHeaderProps ? (
            <DatePickerHeader
              summary={query("sm").windowGt && hint ? hint : ""}
              onChange={datePickerHeaderProps.onChange}
              currentPreset={datePickerHeaderProps.currentPreset}
              dateRangePresets={datePickerHeaderProps.dateRangePresets}
              startDate={datePickerHeaderProps.startDate}
              endDate={datePickerHeaderProps.endDate}
            />
          ) : null}
          <SummaryPanel
            summaryItems={summaryData}
            metricReferenceText={vsLastYear}
            loading={summaryDataLoading}
            onSelect={onSelectMetric}
          />
          <PanelContainer>
            <PanelPadding>
              {hasMainError ? (
                <Callout variant="critical" description={chartDataError ? chartDataError : summaryDataError} />
              ) : null}
              {!hasMainError ? (
                <ProfitabilityChartsContainer>
                  <AdditionalCostsForm additionalCostsDataError={additionalCostsDataError} {...additionalCostsData} />
                  {chartData ? (
                    <ProfitChart
                      {...chartData}
                      thisYearEnabled={selectedThisYearEnabled}
                      lastYearEnabled={selectedLastYearEnabled}
                      salesEnabled={salesEnabled}
                      salesIncReturnsEnabled={salesIncReturnsEnabled}
                      marketingEnabled={marketingEnabled}
                      profitEnabled={profitEnabled}
                      roasEnabled={roasEnabled}
                      aovEnabled={aovEnabled}
                      cacEnabled={cacEnabled}
                      loading={chartDataLoading}
                      temporalResPickerProps={temporalResPickerProps}
                      chartFilterSwitchesProps={chartFilterSwitchesProps}
                    />
                  ) : null}
                </ProfitabilityChartsContainer>
              ) : null}
            </PanelPadding>
          </PanelContainer>
        </DataGroupContainer>
        {enableTabs && pinnedMetricsProps ? (
          <Tabs
            tabs={[
              {
                title:
                  tabTitlePinnedMetrics ??
                  intl.formatMessage({ id: "Overview Pinned Metrics default title", defaultMessage: "Pinned Metrics" }),
                content: (
                  <DataGroupContainer>
                    <PinnedMetricsTabContainer {...pinnedMetricsProps} />
                  </DataGroupContainer>
                ),
              },
              {
                title:
                  tabTitleTable ??
                  intl.formatMessage({
                    id: "Overview Detailed Performance default title",
                    defaultMessage: "Detailed Performance",
                  }),
                content: (
                  <TableComponent
                    tableProps={tableProps}
                    tableDataLoaded={tableDataLoaded}
                    tableDataLoading={tableDataLoading}
                    subtitle={subtitle}
                    tableDataError={tableDataError}
                  />
                ),
              },
            ]}
          />
        ) : (
          <>
            {pinnedMetricsProps ? (
              <DataGroupContainer>
                <PinnedMetrics {...pinnedMetricsProps} />
              </DataGroupContainer>
            ) : null}
            <TableComponent
              tableProps={tableProps}
              tableDataLoaded={tableDataLoaded}
              tableDataLoading={tableDataLoading}
              subtitle={subtitle}
              tableDataError={tableDataError}
            />
          </>
        )}
      </FullWidthPage>
    </AppShell>
  );
};

const TableComponent = ({
  tableProps,
  tableDataLoaded,
  tableDataLoading,
  subtitle,
  tableDataError,
}: {
  tableProps: LegacyTableProps;
  tableDataLoaded: boolean;
  tableDataLoading: boolean;
  subtitle: string;
  tableDataError: string;
}) => {
  return (
    <DataGroupContainer>
      {tableDataLoaded && tableProps.columns?.length ? (
        <InsightsDataTable
          {...tableProps}
          title={subtitle}
          enableCSVDownload={true}
          csvFilename="wayflyer-insights-overview-data.csv"
        />
      ) : null}
      {tableDataLoading && !tableDataError && <Spinner />}
      {tableDataError ? <Callout variant="critical" description={tableDataError} /> : null}
    </DataGroupContainer>
  );
};
